import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, PenLine, CalendarDays, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/Header";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdGVhY2hlciUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3ODY3NDI0Mzh8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t, lang } = useLang();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </span>
            <span className="font-heading font-extrabold text-lg tracking-tight">Brilla</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">{t("landing.login")}</Button></Link>
            <Link to="/register"><Button className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="landing-signup-btn">{t("landing.signup")}</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6">
        <section className="grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-4 py-1.5 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> {lang === "es" ? "Entrenador bilingüe con IA" : "AI Bilingual Coach"}
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t("landing.heroTitle")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">{t("landing.heroSubtitle")}</p>
            <p className="font-heading font-bold text-secondary text-lg">{t("landing.heroPromise")}</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/register">
                <Button size="lg" className="rounded-full bg-secondary hover:bg-secondary/90 h-12 px-7 text-base" data-testid="hero-create-btn">
                  <PenLine className="h-5 w-5" /> {t("landing.createActivity")}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base border-primary/30" data-testid="hero-plan-btn">
                  <CalendarDays className="h-5 w-5" /> {t("landing.planWeek")}
                </Button>
              </Link>
            </div>
            <div className="flex items-baseline gap-3 pt-4">
              <p className="font-heading font-semibold text-foreground">{t("tagline")}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden border border-border shadow-sm">
              <img src={HERO} alt="A diverse early childhood educator interacting with young students in a bright classroom" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-card border border-border rounded-2xl px-5 py-3 shadow-sm">
              <p className="text-sm font-semibold">Teacher says / El maestro dice</p>
              <p className="text-sm text-foreground">“How many apples do you see?”</p>
              <p className="text-sm text-accent italic">“¿Cuántas manzanas ves?”</p>
            </div>
          </motion.div>
        </section>

        <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
          {t("landing.developedBy")}
        </footer>
      </main>
    </div>
  );
}
